import { httpFailure, type PublicFailure } from '@shared/errors'

import { log } from '@/lib/utils/log'

import { APP_VERSION } from '@/constants/changelog'

export type AppError = Error & {
  /** The message without its support reference, for inline field errors. */
  detail?: string
  status?: number
  code?: string
  requestId?: string
  operation?: string
  fieldErrors?: Record<string, string>
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

export function createAppError(message: string, details: Omit<AppError, keyof Error> = {}): AppError {
  return Object.assign(new Error(message), details)
}

/** Gateway references can be padded opaque IDs as well as UUIDs; URLs are never valid references. */
export function safeRequestId(value: unknown): string | undefined {
  return typeof value === 'string' && /^[a-zA-Z0-9_+/-]{1,120}={0,2}$/.test(value) ? value : undefined
}

export function errorMessage(
  error: unknown,
  fallback = 'This action could not be completed. Please try again.'
): string {
  if (error instanceof Error) {
    const known = error as AppError
    if (known.code !== undefined || known.fieldErrors !== undefined) return known.message ? known.message : fallback
    if (known.status) return httpFailure(known.status).message
    // Native browser/provider errors can contain request URLs and implementation details.
    if (error instanceof TypeError) return 'The service could not be reached. Check your connection and try again.'
    return error.message ? error.message : fallback
  }
  return fallback
}

const reportedGenerations = new Set<string>()

export function clearGenerationDiagnostics(entityId?: string) {
  if (!entityId) reportedGenerations.clear()
  else for (const key of reportedGenerations) if (key.startsWith(`${entityId}:`)) reportedGenerations.delete(key)
}

export interface ErrorContext {
  operation: string
  entityId?: string
  requestId?: string
  code?: string
  status?: number
}

const reportedErrors = new WeakSet<object>()

/**
 * Route words such as `character-onboarding` are lowercase letters and hyphens; UUIDs and
 * numbered or encoded values are not, so they become `:id`. Keeping only route-shaped segments
 * needs no list of routes to maintain. A character ID without digits reads like a route word and
 * is kept; character IDs are not sensitive.
 */
export function sanitizeRoute(pathname: string): string {
  return pathname
    .split('/')
    .map((part) => (part === '' || /^[a-z]+(?:-[a-z]+)*$/.test(part) ? part : ':id'))
    .join('/')
}

/** Only allowlisted fields reach the production console; never serialize the thrown object. */
export function reportError(error: unknown, context: ErrorContext): void {
  if (isAbortError(error)) return
  if (typeof error === 'object' && error !== null) {
    if (reportedErrors.has(error)) return
    reportedErrors.add(error)
  }
  const details = error instanceof Error ? (error as AppError) : undefined
  const status = context.status ?? details?.status
  const safeIdentifier = (value: unknown) =>
    typeof value === 'string' && /^[a-zA-Z0-9_.:-]{1,120}$/.test(value) ? value : undefined
  log(context.operation, 'error', {
    timestamp: new Date().toISOString(),
    route: typeof window === 'undefined' ? undefined : sanitizeRoute(window.location.pathname),
    stage: import.meta.env.VITE_STAGE ?? 'local',
    version: APP_VERSION,
    code:
      safeIdentifier(context.code ?? details?.code ?? (details?.name !== 'Error' ? details?.name : undefined)) ??
      'UNEXPECTED_ERROR',
    status:
      typeof status === 'number' && Number.isInteger(status) && status >= 100 && status < 600 ? status : undefined,
    requestId: safeRequestId(context.requestId ?? details?.requestId),
    entityId: safeIdentifier(context.entityId)
  })
}

export function reportGenerationFailure(failure: PublicFailure, entityId: string): void {
  const key = `${entityId}:${failure.code}`
  if (reportedGenerations.has(key)) return
  reportedGenerations.add(key)
  reportError(createAppError(failure.message, { code: failure.code }), { operation: 'Generation failed', entityId })
}
